import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, CheckCircle } from 'lucide-react';
import { projects } from '../../data/mock';

// Local-SEO ItemList schema — each project is an ImageObject with a place.
const ProjectsSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Recent Basin Plumbing Projects in the Permian Basin',
    itemListElement: projects.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'ImageObject',
        name: `${p.title} — ${p.location}`,
        caption: `${p.title} in ${p.location}. ${p.summary}`,
        contentUrl: p.image,
        contentLocation: { '@type': 'Place', name: p.location },
      },
    })),
  };
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
};

export const ProjectsShowcase = () => {
  return (
    <section className="py-20 bg-white" data-testid="projects-showcase">
      <ProjectsSchema />
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-block text-[#e85a24] font-semibold uppercase tracking-wider text-sm mb-2">
            Recent Projects
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mb-4">
            Real Work Across the Permian Basin
          </h2>
          <p className="text-gray-600">
            A look at recent plumbing jobs we've completed for homeowners in Odessa, Midland,
            and surrounding West Texas communities.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((p) => (
            <article
              key={p.id}
              data-testid={`project-${p.id}`}
              className="group rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={p.image}
                  alt={`${p.title} completed by Basin Plumbing in ${p.location}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-[#e85a24] text-white text-xs font-semibold px-3 py-1 rounded-full">
                  <CheckCircle size={12} /> Completed
                </span>
              </div>
              <div className="p-6">
                <span className="text-xs font-semibold text-[#e85a24] uppercase tracking-wide">
                  {p.service}
                </span>
                <h3 className="text-lg font-bold text-[#1a3a5c] mt-1 mb-2">{p.title}</h3>
                <p className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                  <MapPin size={14} className="text-[#e85a24]" /> {p.location}
                </p>
                <p className="text-gray-600 text-sm mb-4">{p.summary}</p>
                <Link
                  to={p.serviceUrl}
                  className="text-[#e85a24] font-medium text-sm inline-flex items-center gap-1 hover:gap-2 transition-all"
                  data-testid={`project-link-${p.id}`}
                >
                  View this service
                  <ArrowRight size={14} />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsShowcase;
